import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import Toast from '../common/Toast';
import HelpWidget from '../support/HelpWidget';
import TrialBanner from '../billing/TrialBanner';
import AddCompanyModal from '../common/AddCompanyModal';
import { useApp } from '../../context/AppContext';

export default function AppLayout({ pageTitle, children, hasDashboardAlert, fullHeight = false, flush = false }) {
  const { isAddCompanyModalOpen, closeAddCompanyModal, companyToEdit } = useApp();
  const location = useLocation();

  // Off-canvas drawer state — only relevant below `lg`, where the sidebar is
  // hidden by default. At `lg`+ the sidebar is always visible (the drawer and
  // backdrop are `lg:hidden`), so this state is inert on desktop.
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close the drawer whenever the route changes (i.e. a nav tap on tablet).
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Close the drawer on Escape while it's open.
  useEffect(() => {
    if (!sidebarOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setSidebarOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [sidebarOpen]);

  return (
    <div className="h-[var(--app-vh)] w-full overflow-hidden" style={{ background: '#f4f0e8' }}>
      <Sidebar
        hasDashboardAlert={hasDashboardAlert}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Drawer backdrop — below lg only, only while open. `fixed inset-0` lets
          index.css size it to the zoom-compensated viewport (--app-vw/vh). */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 z-[45] bg-black/40"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <div className="lg:ml-[260px] flex flex-col h-[var(--app-vh)] min-w-0 overflow-hidden">
        <Header pageTitle={pageTitle} onMenuClick={() => setSidebarOpen(true)} />
        {/* Silent for an account in good standing. */}
        <TrialBanner />
        <main className="flex-1 min-w-0 min-h-0 overflow-y-auto overflow-x-hidden cs-content-scroll">
          {/* Centered container — every page sits inside this so content has
              equal left/right margins on wide screens and isn't left-hugging.
              max-w sized to give breathing room on ultra-wide monitors while
              still letting content fill the area at typical laptop widths.

              Two layout modes:
              • Default (content pages) — wrapper grows with its content, so the
                28px bottom padding stays visible when the page scrolls. Last
                section keeps breathing room above the viewport edge.
              • fullHeight (two-pane doc pages: Minutes, Report Generator) —
                `h-full` gives the wrapper a definite height so a child using
                `height: 100%` fills the viewport and scrolls INTERNALLY rather
                than growing unbounded. These pages manage their own bottom gap. */}
          <div className={flush ? 'h-full' : `max-w-[1440px] mx-auto p-[28px]${fullHeight ? ' h-full' : ''}`}>
            {children}
          </div>
        </main>
      </div>

      <AddCompanyModal open={isAddCompanyModalOpen} onClose={closeAddCompanyModal} initialData={companyToEdit} />
      {/* Bottom-right on every page. Answers product questions from the help
          library and hands anything else to a person. */}
      <HelpWidget />
      <Toast />
    </div>
  );
}
